import { useQuery } from "@tanstack/react-query";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert, Box, Button, Card, CardActionArea, CardContent, Chip, LinearProgress,
  Paper, Stack, Typography,
} from "@mui/material";
import Page from "@/components/Page";
import StatusChip from "@/components/StatusChip";
import { Failed, Loading } from "@/components/DataState";
import { Can } from "@/components/Guard";
import { fetcher } from "@/lib/query";
import { relative } from "@/lib/format";
import { useAuth } from "@/auth/AuthContext";
import { AddIcon } from "../design/icons/AddIcon";
import { BeakerIcon } from "../design/icons/BeakerIcon";
import { HatGraduationIcon } from "../design/icons/HatGraduationIcon";
import { ScanIcon } from "../design/icons/ScanIcon";
import { SparkleIcon } from "../design/icons/SparkleIcon";

interface Allocation {
  sku_id: string; sku_code?: string; sku_name?: string;
  allocated?: number; used_count?: number; remaining?: number;
}
interface PartnerDashboard {
  org: { id: string; name: string; status: string };
  linked: boolean;
  customers: {
    total: number;
    expiring: { customer_id: string; name: string; state: string; days_remaining?: number }[];
  };
  instances: {
    total: number; demos_active: number; demos_allowed: number;
    demo_default_days?: number; demo_max_days?: number;
    list: { _id: string; tenant_name: string; status: string; kind: string; hostname?: string;
            expires_at?: string }[];
  };
  demo_requests_pending: number;
  quota: { linked: boolean; allocations?: Allocation[]; error?: string; note?: string };
  partner_kind?: "ecm" | "scanner" | "both";
  products?: string[];
  trials?: { running: number; allowed: number;
             vouchers: { total: number; redeemed: number; revoked: number; available: number } };
}
interface Opportunity {
  _id: string; customer: { name: string }; status: string; updated_at?: string;
}

function Metric({ label, value, hint, to, tone }: {
  label: string; value: string | number; hint?: string; to?: string;
  tone?: "warning" | "error";
}) {
  const body = (
    <CardContent sx={{ py: 2 }}>
      <Typography variant="overline" color="text.secondary">{label}</Typography>
      <Typography
        variant="h4" fontWeight={600}
        color={tone === "error" ? "error.main" : tone === "warning" ? "warning.main" : undefined}
      >
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary">{hint}</Typography>
      ) : null}
    </CardContent>
  );
  return (
    <Card>
      {to ? <CardActionArea component={RouterLink} to={to}>{body}</CardActionArea> : body}
    </Card>
  );
}

/**
 * A partner's landing page.
 *
 * The vendor overview is an operations queue — what is failing, what is waiting
 * on me to approve it. None of that is a partner's job. What a partner needs on
 * sign-in is: how much of my demo allowance is spent, which of my customers is
 * about to expire, and where do I start the next deal. So this is a separate
 * screen rather than the same one with things hidden.
 */
export default function PartnerOverviewPage() {
  const { me, can } = useAuth();

  const dash = useQuery(fetcher<PartnerDashboard>("/resellers/me"));
  const training = useQuery({
    ...fetcher<{ progress: { completed: number; total: number } }>("/training/courses"),
    enabled: can("training.read"),
  });
  const learnt = training.data?.progress;
  const opportunities = useQuery({
    ...fetcher<{ opportunities: Opportunity[] }>("/sales/opportunities?limit=6"),
    enabled: can("sales.read"),
  });

  const data = dash.data;
  const demosUsed = data?.instances.demos_active ?? 0;
  const demosAllowed = data?.instances.demos_allowed ?? 0;
  const expiring = data?.customers.expiring ?? [];
  const allocations = (data?.quota.allocations ?? []).filter(
    (a) => (a.allocated ?? 0) > 0 || (a.used_count ?? 0) > 0,
  );

  return (
    <Page
      title={`Welcome${me?.user.name ? `, ${me.user.name.split(" ")[0]}` : ""}`}
      subtitle={<>{me?.org.name} · Partner</>}
      actions={
        <Stack direction="row" spacing={1} data-tour="overview-actions">
          <Can perms={["trials.request"]}>
            <Button component={RouterLink} to="/trials" startIcon={<ScanIcon />}>
              Start a trial
            </Button>
          </Can>
          <Can perms={["demos.request"]}>
            <Button component={RouterLink} to="/demos" startIcon={<BeakerIcon />}>
              Request a demo
            </Button>
          </Can>
          <Can perms={["sales.write"]}>
            <Button component={RouterLink} to="/sales" variant="contained" startIcon={<AddIcon />}>
              New opportunity
            </Button>
          </Can>
        </Stack>
      }
    >
        {dash.isLoading ? <Loading /> : null}
      {dash.error ? <Failed error={dash.error} onRetry={() => dash.refetch()} /> : null}

      {data ? (
        <Stack spacing={3}>
          {!data.linked && can("licenses.issue") ? (
            <Alert severity="info">
              Licences cannot be issued from this account yet — MultiDoc has to finish setting
              it up. Proposals, demos and the CRM work already.
            </Alert>
          ) : null}

          {learnt && learnt.total && learnt.completed < learnt.total ? (
            <Paper variant="outlined" sx={{ p: 2.5 }} data-tour="overview-training">
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                <HatGraduationIcon color="primary" sx={{ fontSize: 36 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {learnt.completed ? "Carry on with your training" : "New here? Start with the training"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Short courses on proposals, the CRM and demos, with walkthroughs that point at the
                    real buttons. {learnt.completed} of {learnt.total} lessons done.
                  </Typography>
                  <LinearProgress variant="determinate" value={(learnt.completed / learnt.total) * 100}
                                  sx={{ mt: 1, height: 6, borderRadius: 3, maxWidth: 360 }} />
                </Box>
                <Button variant="contained" component={RouterLink} to="/training" startIcon={<HatGraduationIcon />}>
                  {learnt.completed ? "Continue" : "Start training"}
                </Button>
              </Stack>
            </Paper>
          ) : null}

          {data?.demo_requests_pending ? (
            <Alert severity="info" action={
              <Button size="small" component={RouterLink} to="/demos">Open</Button>
            }>
              {data.demo_requests_pending} demo request
              {data.demo_requests_pending > 1 ? "s are" : " is"} waiting for a MultiDoc decision.
            </Alert>
          ) : null}

          {expiring.length ? (
            <Alert severity="warning" action={
              <Button size="small" component={RouterLink} to="/licenses">Review</Button>
            }>
              {expiring.length} customer licence{expiring.length > 1 ? "s are" : " is"} expiring
              or expired: {expiring.slice(0, 3).map((c) => c.name).join(", ")}
              {expiring.length > 3 ? "…" : ""}
            </Alert>
          ) : null}

          <Box data-tour="overview-metrics" sx={{ display: "grid", gap: 2,
                     gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" } }}>
            <Metric label="Customers" value={data?.customers.total ?? 0} to="/licenses" />
            <Metric
              label="Demos running" value={demosUsed}
              hint={demosAllowed ? `of ${demosAllowed} allowed` : undefined}
              to="/demos"
              tone={demosAllowed && demosUsed >= demosAllowed ? "warning" : undefined}
            />
            <Metric
              label="Trials running" value={data?.trials?.running ?? 0}
              hint={data?.trials?.allowed
                ? `of ${data.trials.allowed} allowed · ${data.trials.vouchers.available} voucher${data.trials.vouchers.available === 1 ? "" : "s"} left`
                : undefined}
              to="/trials"
            />
            <Metric
              label="Expiring" value={expiring.length} to="/licenses"
              tone={expiring.length ? "warning" : undefined}
            />
          </Box>

          <Box sx={{ display: "grid", gap: 3,
                     gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
            <Paper variant="outlined" sx={{ p: 2.5 }} data-tour="overview-allowance">
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                Demo allowance
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                How many demo instances you may have running at once.
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="h4" fontWeight={600}>{demosUsed}</Typography>
                <Typography variant="body2" color="text.secondary">
                  of {demosAllowed || "—"}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={demosAllowed ? Math.min(100, (demosUsed / demosAllowed) * 100) : 0}
                color={demosAllowed && demosUsed >= demosAllowed ? "warning" : "primary"}
              />

              {allocations.length ? (
                <Stack spacing={1} sx={{ mt: 2.5 }}>
                  <Typography variant="overline" color="text.secondary">
                    Licence allocation
                  </Typography>
                  {allocations.slice(0, 6).map((a) => (
                    <Stack key={a.sku_id} direction="row" alignItems="center" spacing={1.5}>
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                        {a.sku_name || a.sku_code || a.sku_id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {a.used_count ?? 0} / {a.allocated ?? 0}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              ) : data?.quota.error ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
                  Allocation is unavailable right now: {data.quota.error}
                </Typography>
              ) : null}
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5 }} data-tour="overview-instances">
              <Stack direction="row" justifyContent="space-between" alignItems="center"
                     sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={600}>Your instances</Typography>
                {/* /instances lands on Customers (production only): a partner
                    with only demos found an empty page behind "All". */}
                <Button size="small" component={RouterLink} to="/demos">All demos</Button>
              </Stack>
              <Stack spacing={1.5}>
                {(data?.instances.list ?? []).slice(0, 6).map((row) => (
                  <Stack key={row._id} direction="row" spacing={1.5} alignItems="center"
                         component={RouterLink}
                         to={row.kind === "demo" ? `/demos/live/${row._id}` : `/production/${row._id}`}
                         sx={{ textDecoration: "none", color: "inherit" }}>
                    <StatusChip status={row.status} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" noWrap>{row.tenant_name}</Typography>
                    </Box>
                    <Chip size="small" variant="outlined" label={row.kind} />
                  </Stack>
                ))}
                {!data?.instances.list.length ? (
                  <Typography variant="body2" color="text.secondary">
                    Nothing running yet. A demo starts with a request.
                  </Typography>
                ) : null}
              </Stack>
            </Paper>
          </Box>

          <Can perms={["sales.read"]}>
            <Paper variant="outlined" sx={{ p: 2.5 }} data-tour="overview-opportunities">
              <Stack direction="row" justifyContent="space-between" alignItems="center"
                     sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <SparkleIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontWeight={600}>Opportunities</Typography>
                </Stack>
                <Button size="small" component={RouterLink} to="/sales">All</Button>
              </Stack>
              <Stack spacing={1.5}>
                {(opportunities.data?.opportunities ?? []).map((o) => (
                  <Stack key={o._id} direction="row" spacing={1.5} alignItems="center"
                         component={RouterLink} to={`/sales/${o._id}`}
                         sx={{ textDecoration: "none", color: "inherit" }}>
                    <Chip size="small" label={o.status} variant="outlined" />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" noWrap>{o.customer.name}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {relative(o.updated_at)}
                    </Typography>
                  </Stack>
                ))}
                {!opportunities.data?.opportunities.length ? (
                  <Typography variant="body2" color="text.secondary">
                    No opportunities yet. Start one and the Console will draft the
                    proposal and deck in your brand.
                  </Typography>
                ) : null}
              </Stack>
            </Paper>
          </Can>
        </Stack>
      ) : null}
    </Page>
  );
}
